import React from "react";
import CrossPlatformDropdown from "../../../../components/CrossPlatformDropdown";
import {
  View,
  StyleSheet,
  Text,
  TextInput,
  Platform,
  Dimensions,
} from "react-native";
import LhDatePicker from "../../../../components/LhDatePicker";
import LhButton from "../../../../components/LhButton";
import { useSelector } from "react-redux";
import { FontFamily } from "../../../../constants/fonts";
import { DLOButton } from "../../../../modules/dlo/components";
import { useTheme } from "../../../../theme";

// Define interface for employee profile details
interface EmployeeDetails {
  name: string;
  peerCoach: string;
  level?: string;
  slotId?: string;
  graduationDate: Date | null;
  hiringDate: Date | null;
}

interface EmployeeProfileFormProps {
  employeeDetails: EmployeeDetails;
  handleEmployeeDetailChange: (
    field: keyof EmployeeDetails,
    value: string | Date | null
  ) => void;
  isFormModified: boolean;
  onSave: () => void;
  isSaving?: boolean;
}

const EmployeeProfileForm: React.FC<EmployeeProfileFormProps> = ({
  employeeDetails,
  handleEmployeeDetailChange,
  isFormModified,
  onSave,
  isSaving = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const isIOS = Platform.OS === "ios";
  const isIPad = isIOS && Dimensions.get("window").width > 768;

  // Get employees from nho slice
  const employees = useSelector((state: any) => state.nho.employees || []);

  // Map employees to dropdown options for Peer Coach
  const peerCoachOptions: { id: string; displayText: string }[] = React.useMemo(() => {
    return employees.map((emp: any) => ({
      id: emp.GPID || emp.ID || '',
      displayText: `${emp.First_Name || ''} ${emp.Last_Name || ''}${emp.GPID ? ' (' + emp.GPID + ')' : ''}`.trim(),
    }));
  }, [employees]);

  return (
    <View
      style={[
        styles.formInputsRow,
        {
          zIndex: 999,
          //marginHorizontal: isIOS ? (isIPad ? 30 : 16) : 0,
          backgroundColor: isDarkTheme
            ? colors.background.surface
            : "white",
        },
      ]}
    >
      {isIOS ? (
        // iOS layout with 2 inputs per row
        <>
          {/* First row - New Hire Name and Peer Coach */}
          <View style={styles.iosInputRow}>
            <View style={styles.iosInputContainer}>
              <View style={styles.labelColumn}>
                <Text
                  style={[
                    styles.formLabel,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  New Hire Name
                </Text>
              </View>
              <View style={styles.inputColumn}>
                <TextInput
                  style={[
                    styles.formInput,
                    {
                      backgroundColor: isDarkTheme
                        ? colors.background.elevated
                        : "#C0C0C0",
                      color: isDarkTheme ? colors.text.primary : "#6C757D",
                      borderColor: isDarkTheme
                        ? colors.border.default
                        : "#ced4da",
                    },
                    Platform.OS === "ios" && Dimensions.get("window").width < 768
                      ? { height: 42} 
                      : {height: 42},
                  ]}
                  placeholder="New Hire Name"
                  value={employeeDetails.name}
                  editable={false}
                />
              </View>
            </View>

            <View style={styles.iosInputContainer}>
              <View style={styles.labelColumn}>
                <Text
                  style={[
                    styles.formLabel,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  Peer Coach
                </Text>
              </View>
              <View style={styles.inputColumn}>
                <CrossPlatformDropdown
                  childList={peerCoachOptions}
                  placeholder="Select Peer Coach"
                  selection="single"
                  selectedValue={
                    peerCoachOptions.find(
                      (opt: { id: string; displayText: string }) => opt.id === employeeDetails.peerCoach
                    ) || null
                  }
                  setSelectedValue={(selected) => {
                    if (selected && !Array.isArray(selected)) {
                      handleEmployeeDetailChange(
                        "peerCoach",
                        String(selected.id)
                      );
                    } else {
                      handleEmployeeDetailChange("peerCoach", "");
                    }
                  }}
                  isClearable={true}
                  isDisabled={false}
                  size="medium"
                />
              </View>
            </View>
          </View>

          {/* Second row - Expected Graduation Date and Date of Hiring */}
          <View style={styles.iosInputRow}>
            <View style={styles.iosInputContainer}>
              <View style={styles.labelColumn}>
                <Text
                  style={[
                    styles.formLabel,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  Expected Graduation Date
                </Text>
              </View>
              <View style={styles.inputColumn}>
                <LhDatePicker
                  value={employeeDetails.graduationDate}
                  onChange={(date) =>
                    handleEmployeeDetailChange("graduationDate", date)
                  }
                  placeholder="Select graduation date"
                  error={false}
                  colorScheme={isDarkTheme ? "dark" : "light"}
                  iconColor={isDarkTheme ? colors.text.primary : "#0066CC"}
                  style={{
                    pickerButtonStyle: {
                      backgroundColor: isDarkTheme
                        ? colors.background.elevated
                        : "#FFFFFF",
                      borderColor: isDarkTheme
                        ? colors.border.default
                        : "#ced4da",
                      borderWidth: 1,
                      borderRadius: 6,
                      height: Platform.OS === "ios" ? 42 : 32,
                    },
                    dateTextStyle: {
                      color: isDarkTheme ? colors.text.primary : "#2D2D2D",
                    },
                    placeholderTextStyle: {
                      color: isDarkTheme ? colors.text.secondary : "#6C757D",
                    },
                  }}
                  // centerPlaceholder
                />
              </View>
            </View>

            <View style={styles.iosInputContainer}>
              <View style={styles.labelColumn}>
                <Text
                  style={[
                    styles.formLabel,
                    isDarkTheme && { color: colors.text.primary },
                  ]}
                >
                  Date of Hiring
                </Text>
              </View>
              <View style={styles.inputColumn}>
                <LhDatePicker
                  value={employeeDetails.hiringDate}
                  onChange={(date) =>
                    handleEmployeeDetailChange("hiringDate", date)
                  }
                  placeholder="Select hiring date"
                  error={false}
                  colorScheme={isDarkTheme ? "dark" : "light"}
                  iconColor={isDarkTheme ? colors.text.primary : "#0066CC"}
                  style={{
                    pickerButtonStyle: {
                      backgroundColor: isDarkTheme
                        ? colors.background.elevated
                        : "#FFFFFF",
                      borderColor: isDarkTheme
                        ? colors.border.default
                        : "#ced4da",
                      borderWidth: 1,
                      borderRadius: 6,
                      height: Platform.OS === "ios" ? 42 : 32,
                    },
                    dateTextStyle: {
                      color: isDarkTheme ? colors.text.primary : "#2D2D2D",
                    },
                    placeholderTextStyle: {
                      color: isDarkTheme ? colors.text.secondary : "#6C757D",
                    },
                  }}
                  // centerPlaceholder
                />
              </View>
            </View>
          </View>

          {/* Save Button */}
          <DLOButton
            text="Save"
            variant="primary"
            size="medium"
            iconTrailing=""
            //iconSize={20}
            onPress={onSave}
            isLoading={isSaving}
            disabled={!isFormModified || isSaving}
            style={
              !isFormModified
                ? styles.iosDisabledButton
                : styles.iosActionButton
            }
          />
        </>
      ) : (
        // Original layout for non-iOS platforms
        <>
          <View style={[styles.inputGroup, { zIndex: 999 }]}>
            <View style={styles.labelColumn}>
              <Text
                style={[
                  styles.formLabel,
                  isDarkTheme && { color: colors.text.primary },
                ]}
              >
                New Hire Name
              </Text>
            </View>
            <View style={styles.inputColumn}>
              <TextInput
                style={[
                  styles.formInput,
                  {
                    backgroundColor: isDarkTheme
                      ? colors.background.elevated
                      : "#C0C0C0",
                    color: isDarkTheme ? colors.text.primary : "#6C757D",
                    borderColor: isDarkTheme
                      ? colors.border.default
                      : "#ced4da",
                  },
                ]}
                placeholder="New Hire Name"
                value={employeeDetails.name}
                editable={false}
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.labelColumn}>
              <Text
                style={[
                  styles.formLabel,
                  isDarkTheme && { color: colors.text.primary },
                ]}
              >
                Peer Coach
              </Text>
            </View>
            <View style={styles.inputColumn}>
              <CrossPlatformDropdown
                childList={peerCoachOptions}
                placeholder="Select Peer Coach"
                selection="single"
                selectedValue={
                  peerCoachOptions.find(
                    (opt: { id: string; displayText: string }) => opt.id === employeeDetails.peerCoach
                  ) || null
                }
                setSelectedValue={(selected) => {
                  if (selected && !Array.isArray(selected)) {
                    handleEmployeeDetailChange(
                      "peerCoach",
                      String(selected.id)
                    );
                  } else {
                    handleEmployeeDetailChange("peerCoach", "");
                  }
                }}
                isClearable={true}
                isDisabled={false}
                size="small"
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.labelColumn}>
              <Text
                style={[
                  styles.formLabel,
                  isDarkTheme && { color: colors.text.primary },
                ]}
              >
                Expected Graduation Date
              </Text>
            </View>
            <View style={styles.inputColumn}>
              <LhDatePicker
                value={employeeDetails.graduationDate}
                onChange={(date) =>
                  handleEmployeeDetailChange("graduationDate", date)
                }
                placeholder="Select graduation date"
                error={false}
                colorScheme={isDarkTheme ? "dark" : "light"}
                iconColor={isDarkTheme ? colors.text.primary : "#0066CC"}
                style={{
                  pickerButtonStyle: {
                    backgroundColor: isDarkTheme
                      ? colors.background.elevated
                      : "#FFFFFF",
                    borderColor: isDarkTheme
                      ? colors.border.default
                      : "#ced4da",
                    borderWidth: 1,
                    borderRadius: 6,
                    height: Platform.OS === "ios" ? 42 : 32,
                  },
                  dateTextStyle: {
                    color: isDarkTheme ? colors.text.primary : "#2D2D2D",
                  },
                  placeholderTextStyle: {
                    color: isDarkTheme ? colors.text.secondary : "#6C757D",
                  },
                }}
                // centerPlaceholder
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.labelColumn}>
              <Text
                style={[
                  styles.formLabel,
                  isDarkTheme && { color: colors.text.primary },
                ]}
              >
                Date of Hiring
              </Text>
            </View>
            <View style={styles.inputColumn}>
              <LhDatePicker
                value={employeeDetails.hiringDate}
                onChange={(date) =>
                  handleEmployeeDetailChange("hiringDate", date)
                }
                placeholder="Select hiring date"
                error={false}
                colorScheme={isDarkTheme ? "dark" : "light"}
                iconColor={isDarkTheme ? colors.text.primary : "#0066CC"}
                style={{
                  pickerButtonStyle: {
                    backgroundColor: isDarkTheme
                      ? colors.background.elevated
                      : "#FFFFFF",
                    borderColor: isDarkTheme
                      ? colors.border.default
                      : "#ced4da",
                    borderWidth: 1,
                    borderRadius: 6,
                    height: Platform.OS === "ios" ? 42 : 32,
                  },
                  dateTextStyle: {
                    color: isDarkTheme ? colors.text.primary : "#2D2D2D",
                  },
                  placeholderTextStyle: {
                    color: isDarkTheme ? colors.text.secondary : "#6C757D",
                  },
                }}
              />
            </View>
          </View>

          <LhButton
            text="Save"
            variant="primary"
            size="small"
            iconTrailing=""
            iconSize={20}
            onPress={onSave}
            isLoading={isSaving}
            disabled={!isFormModified || isSaving}
            style={
              !isFormModified ? styles.disabledButton : styles.actionButton
            }
          />
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  formInputsRow: {
    flexDirection: "row",
    flexWrap: Platform.OS === "web" ? "wrap" : "nowrap",
    gap: 16,
    justifyContent: "space-between",
    padding: 15,
    borderRadius: 0,
    ...(Platform.OS === "ios" && {
      flexDirection: "column", // Stack vertically on iOS
      alignItems: "stretch", // Full width
      backgroundColor: "#f8f9fa",
      padding: 20,
      borderRadius: 0,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0,
      shadowRadius: 0,
      marginTop: 10,
      marginBottom: 0,
    }),
  },
  inputGroup: {
    flex: 1,
    minWidth: 250,
    maxWidth: Platform.OS === "ios" ? "100%" : 300,
    marginBottom: 16,
    marginHorizontal: Platform.OS === "ios" ? 0 : 8,
  },
  labelColumn: {
    marginBottom: 10,
  },
  formLabel: {
    backgroundColor: "transparent",
    fontSize: Platform.OS === "ios" ? 12 : 12,
    fontWeight: "400",
    color: "#3A3A3A",
    padding: 0,
    borderWidth: 0,
    fontStyle: "normal",
    fontFamily: FontFamily.regular,
    //textAlign: "center",
    letterSpacing: 0,
   
  },
  formLabel1: {
    backgroundColor: "#0066cc",
    fontSize: Platform.OS === "ios" ? 16 : 14,
    fontWeight: "600",
    color: "white",
    // Reduce vertical padding for iPhone, keep more for iPad, match web
    paddingVertical:
      Platform.OS === "ios"
        ? Dimensions.get("window").width > 768
          ? 12 // iPad
          : 3 // iPhone
        : 8, // web/other
    paddingHorizontal: 8,
    borderRadius: 4,
    textAlign: "center",
    letterSpacing: 0.5,
  },
  inputColumn: {
    width: "100%",
  },
  formInput: {
    fontSize: Platform.OS === "ios" ? 16 : 15,
    padding: 12,
    backgroundColor: "white",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#ced4da",
    color: "gray",
    width: "100%",
    height: Platform.OS === "ios" ? 48 : 32,
  },
  actionButton: {
    //backgroundColor: "green",
    width:
      Dimensions.get("window").width < 400
        ? 90
        : Platform.OS === "web"
        ? "6%"
        : 120,
    minWidth: Platform.OS === "web" ? 90 : 60,
    fontSize: Dimensions.get("window").width < 400 ? 12 : 16,
    marginTop: 25,
    marginLeft: 10,
  },
  disabledButton: {
    backgroundColor: "#C0C0C0",
    borderColor: "white",
    width:
      Dimensions.get("window").width < 400
        ? 90
        : Platform.OS === "web"
        ? "6%"
        : 120,
    minWidth: Platform.OS === "web" ? 90 : 60,
    fontSize: Dimensions.get("window").width < 400 ? 12 : 16,
    marginTop: 25,
    marginLeft: 10,
    opacity: 0.7,
  },
  iosActionButton: {
    //backgroundColor: "green",
    width: "100%", // Full width on iOS
    minWidth: 60,
    fontSize: 16,
    marginTop: 0,
    marginLeft: 0,
    alignSelf: "center", // Center the button
    height: 50, // Bigger touch target
    borderRadius: 24,
  },
  iosDisabledButton: {
    backgroundColor: "#C0C0C0",
    borderColor: "white",
    width: "100%", // Full width on iOS
    minWidth: 60,
    fontSize: 16,
    marginTop: 0,
    marginLeft: 0,
    alignSelf: "center", // Center the button
    height: 40, // Bigger touch target
    borderRadius: 24,
    opacity: 0.7,
    fontFamily: FontFamily.regular,
    fontWeight: "700",
  },
  iosInputRow: {
    flexDirection: Platform.OS === "ios" && Dimensions.get("window").width > 768 ? "row" : "column",
    justifyContent: "space-between",
    width: "100%",
    gap: 16,
    marginBottom: 0,
  },
  iosInputContainer: {
    flex: 1,
    maxWidth: Platform.OS === "ios" && Dimensions.get("window").width > 768 ? "48%" : "100%", // slightly less than 50% to account for gap
  },
});

export default EmployeeProfileForm;
